import type { CVTemplate } from '~/types'
import type { PremiumPlanId } from '~/types/premium'

/**
 * Remembers a purchase the visitor started before they had an account.
 *
 * The landing funnel lets people generate a CV and buy the Job Search Pack
 * without signing up first. Checkout needs a session, and Supabase may require
 * an e-mail confirmation before one exists, so the intent is parked here and
 * resumed in /auth/callback once the account is live.
 */
const PENDING_PLAN_KEY = 'pendingCheckoutPlan'
const PENDING_CV_KEY = 'pendingCV'
const PENDING_TEMPLATE_KEY = 'pendingCVTemplate'
/** The `guest_cv_generations` id of the pending CV, so /admin/journeys follows it into the account. */
const PENDING_GENERATION_KEY = 'pendingCVGeneration'
/** A Job Search Pack paid for before the account existed; see useCvOffers. */
const PENDING_CLAIM_KEY = 'pendingClaimToken'
/** A job posting compared under a generated CV; the editor's "Tailor to a job" starts from it. */
const PENDING_POSTING_KEY = 'pendingJobPosting'
/** An interview prep previewed before the pack was bought; the pack unlocks it. */
const PENDING_INTERVIEW_KEY = 'pendingInterviewPrep'
/** A Civil Service application to save onto the account being signed into (its "Save to my account", signed out). */
const PENDING_CS_SAVE_KEY = 'pendingCivilServiceSave'
/** The affiliate program page (in the visitor's language) to come back to after signing up or in. */
const PENDING_AFFILIATE_KEY = 'pendingAffiliateJoin'

export function usePendingPurchase() {
  function read(key: string): string | null {
    if (!import.meta.client) return null
    try {
      return localStorage.getItem(key)
    } catch {
      return null
    }
  }

  function write(key: string, value: string) {
    if (!import.meta.client) return
    try {
      localStorage.setItem(key, value)
    } catch {
      // Private mode: the visitor simply has to pick the plan again after signing in.
    }
  }

  function clear(key: string) {
    if (!import.meta.client) return
    try {
      localStorage.removeItem(key)
    } catch {
      /* ignore */
    }
  }

  /** Stores the generated CV so it can be created on the new account. */
  function setPendingCV(cvData: unknown) {
    write(PENDING_CV_KEY, JSON.stringify(cvData))
  }

  /** The PDF layout picked on the landing page, applied when the pending CV is created. */
  function setPendingTemplate(template: CVTemplate) {
    write(PENDING_TEMPLATE_KEY, template)
  }

  /** Reads and forgets the parked template, so it is used for one CV only. */
  function takePendingTemplate(): CVTemplate | undefined {
    const template = read(PENDING_TEMPLATE_KEY) as CVTemplate | null
    clear(PENDING_TEMPLATE_KEY)
    return template || undefined
  }

  function setPendingGeneration(generationId: string | null) {
    if (generationId) write(PENDING_GENERATION_KEY, generationId)
    else clear(PENDING_GENERATION_KEY)
  }

  /** Reads and forgets the parked generation id, so it is linked to one CV only. */
  function takePendingGeneration(): string | undefined {
    const generationId = read(PENDING_GENERATION_KEY)
    clear(PENDING_GENERATION_KEY)
    return generationId || undefined
  }

  /**
   * A pack that is paid for but not yet on an account. Signing up with Google
   * leaves the page, so the token has to survive the round trip to /auth/callback,
   * which finishes the claim. The e-mailed /claim/<token> link is the fallback
   * for anyone who never comes back.
   */
  function setPendingClaimToken(token: string) {
    write(PENDING_CLAIM_KEY, token)
  }

  /** Reads and forgets the parked token, so one purchase is claimed once. */
  function takePendingClaimToken(): string | null {
    const token = read(PENDING_CLAIM_KEY)
    clear(PENDING_CLAIM_KEY)
    return token
  }

  /**
   * The posting the visitor checked their new CV against. Whoever buys the pack
   * after that lands in the editor's tailoring with it already pasted, so the
   * first thing they see is the job they paid to apply for.
   */
  function setPendingPosting(posting: string) {
    write(PENDING_POSTING_KEY, posting)
  }

  function hasPendingPosting(): boolean {
    return !!read(PENDING_POSTING_KEY)
  }

  /** Reads and forgets the parked posting, so it fills the tailoring once. */
  function takePendingPosting(): string | null {
    const posting = read(PENDING_POSTING_KEY)
    clear(PENDING_POSTING_KEY)
    return posting
  }

  /**
   * The interview prep whose preview led to buying the pack. Once the pack is
   * on the account, the editor shows that same prep whole
   * (server/api/guest-export/[token].get.ts unlocks it for a plan), instead
   * of writing a new one.
   */
  function setPendingInterview(token: string) {
    write(PENDING_INTERVIEW_KEY, token)
  }

  function hasPendingInterview(): boolean {
    return !!read(PENDING_INTERVIEW_KEY)
  }

  /** Reads and forgets the parked prep, so it is opened once. */
  function takePendingInterview(): string | null {
    const token = read(PENDING_INTERVIEW_KEY)
    clear(PENDING_INTERVIEW_KEY)
    return token
  }

  /**
   * The Civil Service application whose "Save to my account" was clicked
   * signed out. After signing in (the form, or /auth/callback after Google or
   * a confirmed e-mail) its page opens again with ?save=1 and saves it.
   */
  function setPendingCsSave(token: string) {
    write(PENDING_CS_SAVE_KEY, token)
  }

  /** Reads and forgets the parked application, so it is saved once; its page path, or null. */
  function takePendingCsSavePath(): string | null {
    const token = read(PENDING_CS_SAVE_KEY)
    clear(PENDING_CS_SAVE_KEY)
    return token && /^[0-9a-f-]{36}$/i.test(token) ? `/civil-service/${token}?save=1` : null
  }

  /**
   * "Join the affiliate program" clicked signed out. After signing in (the
   * form, or /auth/callback after Google or a confirmed e-mail) the program
   * page opens again, where joining takes one click. Without this the new
   * partner landed in the CV editor and had to find the program on their own.
   */
  function setPendingAffiliate(path: string) {
    write(PENDING_AFFILIATE_KEY, `${Date.now()}|${path}`)
  }

  /** The parked program page if it is a path on this site and fresh (a day, enough to confirm the e-mail). */
  function pendingAffiliatePath(): string | null {
    const [at, path] = (read(PENDING_AFFILIATE_KEY) ?? '').split('|')
    if (!path || !/^\/[a-z0-9/-]*$/i.test(path)) return null
    return Date.now() - Number(at) < 24 * 60 * 60 * 1000 ? path : null
  }

  function hasPendingAffiliate(): boolean {
    return !!pendingAffiliatePath()
  }

  /** Reads and forgets the parked program page, so it opens once. */
  function takePendingAffiliatePath(): string | null {
    const path = pendingAffiliatePath()
    clear(PENDING_AFFILIATE_KEY)
    return path
  }

  function setPendingPlan(planId: PremiumPlanId) {
    write(PENDING_PLAN_KEY, planId)
  }

  function getPendingPlan(): PremiumPlanId | null {
    return (read(PENDING_PLAN_KEY) as PremiumPlanId | null) || null
  }

  function clearPendingPlan() {
    clear(PENDING_PLAN_KEY)
  }

  return {
    setPendingCV,
    setPendingTemplate,
    takePendingTemplate,
    setPendingGeneration,
    takePendingGeneration,
    setPendingClaimToken,
    takePendingClaimToken,
    setPendingPosting,
    hasPendingPosting,
    takePendingPosting,
    setPendingInterview,
    hasPendingInterview,
    takePendingInterview,
    setPendingCsSave,
    takePendingCsSavePath,
    setPendingAffiliate,
    hasPendingAffiliate,
    takePendingAffiliatePath,
    setPendingPlan,
    getPendingPlan,
    clearPendingPlan,
    PENDING_PLAN_KEY,
    PENDING_CV_KEY,
  }
}
